/*
 * KAS LUXURY — the ring size drives the price, invisibly, and has to be chosen.
 *
 * THE PROBLEM THIS SOLVES
 * Pricing a larger ring higher needs a variant, because only a variant carries a price.
 * But a customer should never have to understand "size bands" -- they should pick their
 * size and see the right price. Showing a Size band selector AND a Ring size dropdown
 * asked the customer the same question twice, which is the confusion this removes.
 *
 * HOW IT WORKS
 * The "Size band" variant option stays as the pricing mechanism but is hidden from the
 * page. When the customer picks a ring size, this works out which band that size falls
 * into and selects it for them. The theme then updates the price and the variant exactly
 * as if they had chosen it by hand. The exact size still travels with the order as a line
 * item property, so production gets "US 7.25", not "US 7.25 - 8.5".
 *
 * WHY THE SIZE IS NOW COMPULSORY  (7 Oct 2026)
 * Hiding the band left the CHEAPEST band selected by default. A customer who never
 * touched the size dropdown could buy at the US 3-5 price and then ask for a US 9: a
 * silent undercharge of up to $100 on a ring and $160 on a men's ring. The band IS the
 * price, so the thing that chooses it cannot be optional. Both ways off this page are
 * now closed until a real size is selected:
 *
 *   - Add to Bag. Blocked three times over -- the button is disabled, and submit and
 *     click are both caught in the CAPTURE phase on the document, so they run before any
 *     handler the theme attached to the form or to the button.
 *   - The express checkout button. It posts straight to Shopify's checkout and runs none
 *     of this page's checks, so it is removed from the page by CSS until a size is
 *     chosen. Hiding is deliberate: a dynamic checkout button is Shopify's own element
 *     and the only dependable way to stop it is for it not to be there.
 *
 * Taking the Add to Bag button's disabled state over is safe here BECAUSE EVERY RING
 * VARIANT IS SET TO CONTINUE SELLING WHEN OUT OF STOCK, so the theme never disables that
 * button for a sold-out ring and the two cannot fight. The flag below records that we
 * were the one who disabled it, so a button the theme disabled is never re-enabled.
 *
 * Note what is NOT the fix: marking the dropdown `required`. The theme renders the cart
 * form with `novalidate`, so browser validation is switched off and `required` would
 * have looked like a fix while changing nothing.
 *
 * THE BANDS ARE READ FROM THE PAGE, NOT HARD-CODED.
 * Each band option is labelled "US 3 - 5", "US 8.75 - 10" and so on, so the bounds are
 * parsed out of those labels. Add, rename or re-price a band in Shopify and this keeps
 * working with no code change. A label that does not match that shape is skipped rather
 * than guessed at. That is how men's rings get six bands and the rest four, from one file.
 *
 * FAIL-SAFE
 * The band selector is hidden, and the gate installed, ONLY once a working mapping has
 * been built. If anything is missing -- no bands, no size dropdown, unparseable labels --
 * the band selector stays visible, NO gate is installed, and the customer picks the band
 * themselves, which still prices the ring correctly. A broken script must never leave a
 * product unbuyable. That is not theoretical: the first version of this file shipped with
 * doubled backslashes in these regexes, matched nothing and bailed out, and because of
 * this rule the only visible symptom was that the band selector did not disappear.
 *
 * Every element is looked up BY NAME on each pass instead of being held in a variable, so
 * a re-render by the theme's variant picker can never leave this file writing to a node
 * that is no longer on the page.
 */
(function () {
  'use strict';

  var SIZE_LABEL = 'Ring size (US)';
  var BAND_OPTION = 'Size band';
  var BAND_NAME = 'options[' + BAND_OPTION + ']';
  var PROPERTY_NAME = 'properties[' + SIZE_LABEL + ']';
  var CART_FORM = 'form[action*="/cart/add"]';
  var NOTE_ID = 'kas-size-note';
  var NOTE_TEXT = 'Choose your ring size first. A larger ring uses more gold, ' +
                  'so the price changes with the size.';

  // THIS WHOLE FILE CONTAINS NOT ONE BACKSLASH, ON PURPOSE. Do not "tidy" these classes
  // back into the usual shorthand for a digit or for whitespace.
  //
  // The file reaches the store inside a GraphQL block string, which passes backslashes
  // through verbatim, while the layers above it each hold an opinion about how many
  // backslashes to keep. That has broken this exact file twice: the first version shipped
  // a doubled backslash and matched nothing, and the second did it again from the other
  // direction. Spelling the classes out longhand removes the hazard completely instead of
  // getting the count right once -- and it also means the stored byte count can be
  // compared straight against the local file, which is how the second break was caught.
  //
  // Nothing is given up. [0-9] is precisely what the digit shorthand means in JavaScript,
  // and [.] is precisely an escaped full stop. The space class is narrower than the
  // whitespace shorthand, which is correct here: these labels are written by
  // add_size_bands.py as "US 7.25 - 8.5", with ordinary single spaces.
  var NUMBER = /([0-9]+(?:[.][0-9]+)?)/;
  var RANGE = /([0-9]+(?:[.][0-9]+)?)[ ]*[-–—][ ]*([0-9]+(?:[.][0-9]+)?)/;

  function num(value) {
    var m = String(value).match(NUMBER);
    return m ? parseFloat(m[1]) : null;
  }

  // "US 7.25 - 8.5" -> { lo: 7.25, hi: 8.5 }
  function parseBand(label) {
    var m = String(label).match(RANGE);
    if (!m) {
      return null;
    }
    var lo = parseFloat(m[1]);
    var hi = parseFloat(m[2]);
    return hi >= lo ? { lo: lo, hi: hi } : null;
  }

  function findSelect(name) {
    var all = document.querySelectorAll('select');
    for (var i = 0; i < all.length; i++) {
      if (all[i].name === name) {
        return all[i];
      }
    }
    return null;
  }

  function optionRow(el) {
    return el.closest('.product__option') ||
           el.closest('[data-product-option]') ||
           el.parentElement;
  }

  // The note belongs under the whole dropdown block, not inside the select's own wrapper.
  function noteAnchor(el) {
    return el.closest('.product__custom-option') || optionRow(el);
  }

  function addButton() {
    return document.querySelector(CART_FORM + ' [data-add-to-cart]') ||
           document.querySelector(CART_FORM + ' [name="add"]');
  }

  function start() {
    var bandSelect = findSelect(BAND_NAME);
    var sizeSelect = findSelect(SIZE_LABEL);

    // Not a ring page. Install nothing, so earrings and chains are untouched.
    if (!bandSelect || !sizeSelect) {
      return;
    }

    var bands = [];
    for (var i = 0; i < bandSelect.options.length; i++) {
      var range = parseBand(bandSelect.options[i].value);
      if (range) {
        bands.push({ value: bandSelect.options[i].value, lo: range.lo, hi: range.hi });
      }
    }
    if (!bands.length) {
      return;
    }
    bands.sort(function (a, b) { return a.lo - b.lo; });

    function bandFor(size) {
      for (var i = 0; i < bands.length; i++) {
        if (size >= bands[i].lo && size <= bands[i].hi) {
          return bands[i].value;
        }
      }
      // Outside every band: clamp, so a size can never be unbuyable.
      return size < bands[0].lo ? bands[0].value : bands[bands.length - 1].value;
    }

    // null until the customer picks a real size. "Choose your size" holds no number.
    function sizeNow() {
      var s = findSelect(SIZE_LABEL);
      return s ? num(s.value) : null;
    }

    function note() {
      var existing = document.getElementById(NOTE_ID);
      if (existing) {
        return existing;
      }
      var s = findSelect(SIZE_LABEL);
      if (!s) {
        return null;
      }
      var anchor = noteAnchor(s);
      if (!anchor || !anchor.parentNode) {
        return null;
      }
      var p = document.createElement('p');
      p.id = NOTE_ID;
      p.className = 'kas-size-note';
      p.textContent = NOTE_TEXT;
      anchor.parentNode.insertBefore(p, anchor.nextSibling);
      s.setAttribute('aria-describedby', NOTE_ID);
      return p;
    }

    // Marks the page blocked or clear. The attribute goes on <html> as well as on the
    // form, because the price sits outside the form and needs the same state.
    function refresh() {
      var ok = sizeNow() !== null;
      var state = ok ? 'ok' : 'blocked';

      document.documentElement.setAttribute('data-kas-size-gate', state);
      var forms = document.querySelectorAll(CART_FORM);
      for (var i = 0; i < forms.length; i++) {
        forms[i].setAttribute('data-kas-size-gate', state);
      }

      var n = note();
      if (n) {
        n.setAttribute('data-kas-show', ok ? 'false' : 'true');
      }

      var btn = addButton();
      if (btn) {
        if (!ok) {
          if (!btn.disabled) {
            btn.disabled = true;
            btn.setAttribute('data-kas-gated', '1');
          }
          btn.setAttribute('aria-disabled', 'true');
        } else if (btn.getAttribute('data-kas-gated') === '1') {
          btn.disabled = false;
          btn.removeAttribute('data-kas-gated');
          btn.removeAttribute('aria-disabled');
        }
      }

      // Keep the hidden in-form property in step with the visible dropdown, so the size
      // written on the order can never disagree with the size the customer picked, even
      // if the theme's own copying between the two ever fails.
      var visible = findSelect(SIZE_LABEL);
      var prop = findSelect(PROPERTY_NAME);
      if (visible && prop && prop.value !== visible.value) {
        prop.value = visible.value;
      }

      return ok;
    }

    function apply() {
      var size = sizeNow();
      if (size === null) {
        refresh();
        return;
      }
      var band = findSelect(BAND_NAME);
      if (band) {
        var wanted = bandFor(size);
        if (band.value !== wanted) {
          band.value = wanted;
          band.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      refresh();
    }

    function isCartForm(el) {
      return !!(el && el.getAttribute &&
                String(el.getAttribute('action') || '').indexOf('/cart/add') > -1);
    }

    function stop(e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      refresh();
      var s = findSelect(SIZE_LABEL);
      if (!s) {
        return;
      }
      var anchor = noteAnchor(s);
      if (anchor && anchor.scrollIntoView) {
        anchor.scrollIntoView({ block: 'center' });
      }
      try {
        s.focus();
      } catch (err) {
        // Focus is a courtesy. It must never be the reason the block fails to hold.
      }
    }

    // Capture phase, on the document, so these run before any handler the theme put on
    // the form or the button, and so a cart form added to the page later is covered too.
    document.addEventListener('submit', function (e) {
      if (sizeNow() !== null) {
        return;
      }
      if (!isCartForm(e.target)) {
        return;
      }
      stop(e);
    }, true);

    document.addEventListener('click', function (e) {
      if (sizeNow() !== null) {
        return;
      }
      var btn = e.target && e.target.closest ?
                e.target.closest('[data-add-to-cart], [name="add"]') : null;
      if (!btn || !btn.closest(CART_FORM)) {
        return;
      }
      stop(e);
    }, true);

    // Any change in the buy area re-asserts the band and the gate. Picking a metal AFTER
    // a size must never drop the price back to the cheapest band.
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || t.tagName !== 'SELECT') {
        return;
      }
      if (t.name === BAND_NAME) {
        refresh();   // our own write landing. Do not recurse.
        return;
      }
      apply();
    }, true);

    apply();
    // The theme can re-run its variant picker after DOMContentLoaded.
    window.addEventListener('load', apply);

    var row = optionRow(bandSelect);
    if (row) {
      row.setAttribute('data-kas-hidden-band', 'true');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}());